import { useCallback, useRef, useState } from 'react';

export interface UseDirtySnapshotOptions {
  /**
   * false면 isDirty가 항상 false — 같은 폼이 dirty 게이트 비대상 모드(생성 마법사 등)로도
   * 쓰일 때 호출부 분기 없이 끌 수 있다. 기본 true.
   */
  enabled?: boolean;
}

export interface UseDirtySnapshotResult {
  /** 마지막 capture 시점과 현재 스냅샷이 다른가. capture 전에는 항상 false. */
  isDirty: boolean;
  /**
   * 현재 스냅샷을 기준점으로 잡는다 — 모달 열림(로드 완료 후)과 저장 성공 시점에 부른다.
   * 로드가 비동기라면 반드시 **로드 완료 후**에 불러야 한다(빈 폼을 기준으로 잡으면
   * 로드된 값 전부가 변경으로 판정된다).
   */
  capture: () => void;
  /** 기준점을 버린다(isDirty=false로 복귀) — 모달 닫힘/폼 리셋 시. */
  reset: () => void;
}

/**
 * 수정 모달 dirty 판정 표준 부품 — 캡처 시점 스냅샷 vs 현재 스냅샷의 JSON 직렬화 비교.
 * (결정 기록 `docs/decisions/2026-08-16-edit-modal-dirty-gate-standard.md` —
 * `Organization.tsx` isCompanyDirty형 스냅샷 비교를 모달용 훅으로 추출한 것)
 *
 * 사용 규약:
 * - `getSnapshot`은 **편집 가능한 입력 전수**를 담은 리터럴 객체를 **일정한 키 순서로**
 *   만들어 반환해야 한다. 비교가 `JSON.stringify` 문자열 동등이라 키 순서가 렌더마다
 *   흔들리면 무변경인데 dirty로 판정된다(리터럴로 한 곳에서 만들면 순서는 저절로 고정된다 —
 *   별도 정렬 유틸은 두지 않는다). 필드를 하나라도 빠뜨리면 "바꿨는데 저장이 안 눌리는"
 *   반대 방향 결함이 되므로, 폼 본문이 노출하는 입력과 1:1 대조해 구성할 것.
 * - 스냅샷 값은 **저장이 실제로 보내는 형태로 정규화(trim 등)한 값**을 담아야 한다. 화면
 *   입력 그대로(raw)를 담으면, 저장이 `name.trim()`으로 보내는데 공백만 덧붙여도 스냅샷은
 *   달라져 dirty 게이트가 활성화되고 결국 서버로는 같은 값을 보내는 no-op write가 나간다.
 *   저장 payload를 만드는 변환과 동일한 변환을 스냅샷에도 적용할 것.
 * - `File`/`Date`/`Map`/`Set`은 `JSON.stringify`가 구분하지 못하니(각각 `{}`, ISO 문자열,
 *   `{}`) 스냅샷에는 **원시 형태로 환원**해 담을 것 — File은 `name`+`size`+`lastModified`,
 *   Date는 `getTime()`(또는 ISO), Map/Set은 배열로. 그렇지 않으면 서로 다른 값이 같은
 *   문자열로 뭉개져 변경을 놓친다.
 * - `getSnapshot` 자체는 매 렌더 새 함수여도 된다(내부에서 ref로 최신만 읽는다).
 * - 게이트 배선: 저장 버튼 `disabled={... || !isDirty}` + 푸터 "변경사항 있습니다/없음" 표시
 *   (결정 `2026-08-16-edit-modal-dirty-gate-standard`) + 닫기 보호는 base `Modal`의 `isDirty` prop으로.
 *
 * @example
 * const dirty = useDirtySnapshot(() => ({ name, address }), { enabled: isEditMode });
 * useEffect(() => { if (isEditMode && !isFetching) dirty.capture(); }, [isEditMode, isFetching]);
 * <Modal isDirty={dirty.isDirty} ...>
 *   <Button type="submit" disabled={!canSubmit || !dirty.isDirty}>저장</Button>
 */
export function useDirtySnapshot<T>(
  getSnapshot: () => T,
  options: UseDirtySnapshotOptions = {},
): UseDirtySnapshotResult {
  const { enabled = true } = options;

  // capture()가 안정 참조를 유지하면서 항상 최신 스냅샷을 읽도록 ref로 넘겨 받는다.
  const getSnapshotRef = useRef(getSnapshot);
  getSnapshotRef.current = getSnapshot;

  // 기준점은 직렬화된 문자열로 보관한다 — 비교도 문자열 동등이라 원본 객체를 들고 있을 이유가 없다.
  const [baseline, setBaseline] = useState<string | null>(null);

  const capture = useCallback(() => {
    setBaseline(JSON.stringify(getSnapshotRef.current()));
  }, []);

  const reset = useCallback(() => {
    setBaseline(null);
  }, []);

  const isDirty =
    enabled && baseline !== null && JSON.stringify(getSnapshot()) !== baseline;

  return { isDirty, capture, reset };
}
